import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import { FlagsProvider } from '@atlaskit/flag';
import UploadIcon from '@atlaskit/icon/core/migration/cloud-arrow-up--upload';
import { PackageIntlProvider } from '../../common/ui/intl-provider';
import { AttachmentsFilmstripView, AttachmentsUpload, useAttachments } from './jira-issue-field-attachment';
import messages from './messages';
import { AttachmentFieldContainer, BrowseDropzoneArea, FieldDropzoneContainer, IconContainer, MediaContainer } from './styled';
import { getFieldDropZoneContainerID } from './utils';

// This field gets rendered on the request create and issue create general/direct screens
var AttachmentFieldForUploading = function AttachmentFieldForUploading(props) {
  var _mediaUploadAndViewCo, _mediaUploadAndViewCo2;
  var setUploadingMedia = props.setUploadingMedia,
    mediaUploadAndViewContexts = props.mediaUploadAndViewContexts,
    _props$fieldId = props.fieldId,
    fieldId = _props$fieldId === void 0 ? '' : _props$fieldId,
    projectId = props.projectId,
    portalId = props.portalId,
    maxAllowedTotalAttachmentsSize = props.maxAllowedTotalAttachmentsSize,
    initialFiles = props.initialFiles,
    onAttachmentSizeValidation = props.onAttachmentSizeValidation,
    onAttachmentUpdate = props.onAttachmentUpdate,
    blockFormSubmission = props.blockFormSubmission,
    shouldResetAttachments = props.shouldResetAttachments,
    numberOfAttachmentsOnForm = props.numberOfAttachmentsOnForm;

  // Called when the attachments in useAttachment are changed
  // eg. new attachment added, existing attachment removed
  var handleAttachmentUpdate = useCallback(function (mediaFiles) {
    onAttachmentUpdate === null || onAttachmentUpdate === void 0 || onAttachmentUpdate(mediaFiles);
  }, [onAttachmentUpdate]);
  var _useAttachments = useAttachments({
      mediaUploadAndViewContexts: mediaUploadAndViewContexts,
      projectId: projectId,
      portalId: portalId,
      maxAllowedTotalAttachmentsSize: maxAllowedTotalAttachmentsSize,
      onAttachmentSizeValidation: onAttachmentSizeValidation,
      numberOfAttachmentsOnForm: numberOfAttachmentsOnForm,
      onAttachmentUpdate: handleAttachmentUpdate
    }),
    uploadedFiles = _useAttachments.uploadedFiles,
    existingFiles = _useAttachments.existingFiles,
    onUploadStart = _useAttachments.onUploadStart,
    onUploadError = _useAttachments.onUploadError,
    mediaUploadClientConfig = _useAttachments.mediaUploadClientConfig,
    mediaReadClientConfig = _useAttachments.mediaReadClientConfig,
    uploadConfig = _useAttachments.uploadConfig,
    onRemoveFile = _useAttachments.onRemoveFile,
    onUploadEnd = _useAttachments.onUploadEnd,
    isUploading = _useAttachments.isUploading,
    isDefaultValueInitialized = _useAttachments.isDefaultValueInitialized,
    setDefaultValueInitialized = _useAttachments.setDefaultValueInitialized,
    setExistingFiles = _useAttachments.setExistingFiles,
    setAttachments = _useAttachments.setAttachments,
    isOnDrag = _useAttachments.isOnDrag,
    setIsOnDrag = _useAttachments.setIsOnDrag;
  // setting DefaultValueInitialized only when receiving default Value to guarantee we will set DefaultValue even if it's loaded with some delay.
  useEffect(function () {
    if (existingFiles && !isDefaultValueInitialized) {
      setExistingFiles(initialFiles || []);
    }
    setDefaultValueInitialized(true);
  }, [setExistingFiles, initialFiles, isDefaultValueInitialized, setDefaultValueInitialized, existingFiles]);
  useEffect(function () {
    if (shouldResetAttachments) {
      setAttachments([]);
    }
  }, [setAttachments, shouldResetAttachments]);
  useEffect(function () {
    setUploadingMedia(isUploading);
  }, [isUploading, blockFormSubmission, setUploadingMedia]);
  if (!mediaUploadAndViewContexts) {
    return null;
  }
  var onAttachmentUploadStart = function onAttachmentUploadStart(uploadStartPayload) {
    onUploadStart(uploadStartPayload);
    setIsOnDrag(false);
  };
  var onDragEnter = function onDragEnter() {
    setIsOnDrag(true);
  };
  var onDragLeave = function onDragLeave() {
    setIsOnDrag(false);
  };
  var getAttachmentsUpload = function getAttachmentsUpload() {
    return /*#__PURE__*/React.createElement(AttachmentsUpload, {
      mediaClientConfig: mediaUploadClientConfig,
      uploadConfig: uploadConfig,
      onUploadError: onUploadError,
      onUploadStart: onAttachmentUploadStart,
      onUploadEnd: onUploadEnd,
      dropzoneContainer: document.getElementById(getFieldDropZoneContainerID(fieldId)),
      onDragEnter: onDragEnter,
      onDragLeave: onDragLeave
    }, function (_ref) {
      var setIsBrowserOpen = _ref.setIsBrowserOpen;
      return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.browseWithDropzone, {
        values: {
          a: function a(s) {
            return /*#__PURE__*/React.createElement(Button, {
              spacing: "none",
              appearance: "link",
              onClick: function onClick() {
                return setIsBrowserOpen(true);
              }
            }, "\xA0", s);
          }
        }
      }));
    });
  };
  return /*#__PURE__*/React.createElement(AttachmentFieldContainer, {
    id: getFieldDropZoneContainerID(fieldId)
  }, /*#__PURE__*/React.createElement(FieldDropzoneContainer, {
    "data-testid": "proforma-attachment-field-edit"
  }, /*#__PURE__*/React.createElement(BrowseDropzoneArea, null, /*#__PURE__*/React.createElement(IconContainer, null, /*#__PURE__*/React.createElement(UploadIcon, {
    color: "currentColor",
    spacing: "spacious",
    label: "Upload"
  })), getAttachmentsUpload())), (uploadedFiles.length + existingFiles.length > 0 || isOnDrag) && /*#__PURE__*/React.createElement(MediaContainer, null, /*#__PURE__*/React.createElement(AttachmentsFilmstripView, {
    mediaClientConfigRead: mediaReadClientConfig,
    mediaClientConfigUpload: mediaUploadClientConfig,
    existingFiles: existingFiles,
    uploadedFiles: uploadedFiles,
    onRemoveFile: onRemoveFile,
    collectionNameUpload: ((_mediaUploadAndViewCo = mediaUploadAndViewContexts.uploadContext) === null || _mediaUploadAndViewCo === void 0 ? void 0 : _mediaUploadAndViewCo.collection) || '',
    collectionNameExisting: ((_mediaUploadAndViewCo2 = mediaUploadAndViewContexts.viewContext) === null || _mediaUploadAndViewCo2 === void 0 ? void 0 : _mediaUploadAndViewCo2.collection) || '',
    isOnDrag: isOnDrag
  })));
};
export var AttachmentFieldForUploadingWrapper = function AttachmentFieldForUploadingWrapper(props) {
  var _useIntl = useIntl(),
    locale = _useIntl.locale;
  return /*#__PURE__*/React.createElement(PackageIntlProvider, {
    locale: locale
  }, /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(AttachmentFieldForUploading, props)));
};